/**
 * Ruleset Registry
 *
 * Central configuration for all supported rulesets.
 * Each ruleset defines what data sources it includes and can extend parent rulesets.
 */

export interface RulesetConfig {
  /** Unique identifier for the ruleset */
  id: string;
  /** Display name */
  name: string;
  /** Description shown in UI */
  description: string;
  /** Parent ruleset to inherit data from */
  extends?: string;
  /** Data sources to include (order matters for overrides - later sources override earlier) */
  sources: string[];
  /** Default starting level for new characters */
  startingLevel?: number;
}

export const rulesetRegistry: Record<string, RulesetConfig> = {
  'srd-5.1': {
    id: 'srd-5.1',
    name: 'SRD 5.1',
    description: 'D&D 5th Edition System Reference Document',
    sources: ['srd-5.1'],
    startingLevel: 1,
  },
  ignus: {
    id: 'ignus',
    name: 'Ignus',
    description: 'The Dreams of Ignus homebrew setting by Chris Dumoulin',
    extends: 'srd-5.1',
    sources: ['srd-5.1', 'ignus'], // Inherits SRD, adds Ignus content
    startingLevel: 1, // Standard level 1 start
  },
};

/**
 * Get a ruleset configuration by ID
 */
export function getRulesetConfig(rulesetId: string): RulesetConfig | undefined {
  return rulesetRegistry[rulesetId];
}

/**
 * Get all available ruleset IDs
 */
export function getAvailableRulesetIds(): string[] {
  return Object.keys(rulesetRegistry);
}

/**
 * Get all available rulesets as an array
 */
export function getAvailableRulesets(): RulesetConfig[] {
  return Object.values(rulesetRegistry);
}

/**
 * Check if a source is included in a ruleset
 */
export function isSourceInRuleset(source: string, rulesetId: string): boolean {
  const config = rulesetRegistry[rulesetId];
  if (!config) return false;
  return config.sources.includes(source);
}

export { rulesetFeatures, getRulesetFeatures, type RulesetFeatures } from './features';
